import {Mesh, VECTOR3} from '../../index.js';

/**
 * Creates Hypercube.js box meshes.
 * @example
 * const meshbox = new MeshBox(name, lengths);
 */
class MeshBox extends Mesh {

    /**
     * Stores the memoized data.
     * @type {Object.<string, number[]>}
     * @private
     * @readonly
     * @static
     */
    static $memory = {};

    /**
     * Stores the indices.
     * @type {number[]}
     * @protected
     */
    $indices = [

        // -x face
        0, 1, 2,
        2, 3, 0,

        // +x face
        4, 5, 6,
        6, 7, 4,

        // -y face
        8, 9, 10,
        10, 11, 8,

        // +y face
        12, 13, 14,
        14, 15, 12,

        // -z face
        16, 17, 18,
        18, 19, 16,

        // +z face
        20, 21, 22,
        22, 23, 20
    ];

    /**
     * Stores the normals.
     * @type {number[]}
     * @protected
     */
    $normals = [

        // -x face
        -1, 0, 0,
        -1, 0, 0,
        -1, 0, 0,
        -1, 0, 0,

        // +x face
        1, 0, 0,
        1, 0, 0,
        1, 0, 0,
        1, 0, 0,

        // -y face
        0, -1, 0,
        0, -1, 0,
        0, -1, 0,
        0, -1, 0,

        // +y face
        0, 1, 0,
        0, 1, 0,
        0, 1, 0,
        0, 1, 0,

        // -z face
        0, 0, -1,
        0, 0, -1,
        0, 0, -1,
        0, 0, -1,

        // +z face
        0, 0, 1,
        0, 0, 1,
        0, 0, 1,
        0, 0, 1
    ];

    /**
     * Creates a new Hypercube.js box mesh.
     * @param {string} $name The name of the mesh to create.
     * @param {vector3} $lengths The lengths of the box to create.
     */
    constructor($name, $lengths) {

        super($name);

        this.$type = 'mesh.box' + JSON.stringify($lengths);

        if (MeshBox.$memory.hasOwnProperty(this.$type) === false) {

            MeshBox.$memory[this.$type] = this.$createpositions(VECTOR3.clone($lengths));
        }

        this.$positions = [...MeshBox.$memory[this.$type]];
    }

    /**
     * Creates the positions.
     * @param {vector3} $lengths The lengths of the box to create.
     * @returns {number[]}
     * @private
     */
    $createpositions($lengths) {

        const [xlength, ylength, zlength] = $lengths;

        const xlengthhalved = xlength * 0.5;
        const ylengthhalved = ylength * 0.5;
        const zlengthhalved = zlength * 0.5;

        return [

            // -x face
            - xlengthhalved, - ylengthhalved, - zlengthhalved,
            - xlengthhalved, ylengthhalved, - zlengthhalved,
            - xlengthhalved, ylengthhalved, zlengthhalved,
            - xlengthhalved, - ylengthhalved, zlengthhalved,

            // +x face
            xlengthhalved, - ylengthhalved, - zlengthhalved,
            xlengthhalved, - ylengthhalved, zlengthhalved,
            xlengthhalved, ylengthhalved, zlengthhalved,
            xlengthhalved, ylengthhalved, - zlengthhalved,

            // -y face
            - xlengthhalved, - ylengthhalved, - zlengthhalved,
            - xlengthhalved, - ylengthhalved, zlengthhalved,
            xlengthhalved, - ylengthhalved, zlengthhalved,
            xlengthhalved, - ylengthhalved, - zlengthhalved,

            // +y face
            - xlengthhalved, ylengthhalved, - zlengthhalved,
            xlengthhalved, ylengthhalved, - zlengthhalved,
            xlengthhalved, ylengthhalved, zlengthhalved,
            - xlengthhalved, ylengthhalved, zlengthhalved,

            // -z face
            - xlengthhalved, - ylengthhalved, - zlengthhalved,
            xlengthhalved, - ylengthhalved, - zlengthhalved,
            xlengthhalved, ylengthhalved, - zlengthhalved,
            - xlengthhalved, ylengthhalved, - zlengthhalved,

            // +z face
            - xlengthhalved, - ylengthhalved, zlengthhalved,
            - xlengthhalved, ylengthhalved, zlengthhalved,
            xlengthhalved, ylengthhalved, zlengthhalved,
            xlengthhalved, - ylengthhalved, zlengthhalved
        ];
    }
}

export {

    MeshBox
};

export default MeshBox;
